import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldAlert, Loader2, CheckCircle2, XCircle, Flag, Play } from 'lucide-react';

/**
 * AdminModerationTab — Phase 7C admin surface.
 * - Toggle any moderation rule on/off (hard-reject or soft-flag).
 * - Dry-run "test my copy" box: paste text, see what the engine detects.
 */
const AdminModerationTab = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [scanForm, setScanForm] = useState({ title: '', description: '', body: '' });
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/moderation/rules');
      setRules(data.rules || []);
    } catch (e) {
      toast.error('Failed to load rules', { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, enabled) => {
    setBusyKey(key);
    try {
      await api.patch(`/admin/moderation/rules/${key}`, { enabled });
      setRules(rs => rs.map(r => r.key === key ? { ...r, enabled } : r));
      toast.success(`${key} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error('Toggle failed', { description: e?.response?.data?.detail || e.message });
    } finally { setBusyKey(''); }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post('/admin/moderation/scan', scanForm);
      setScanResult(data);
    } catch (e) {
      toast.error('Scan failed');
    } finally { setScanning(false); }
  };

  const hardRules = rules.filter(r => r.action === 'hard_reject');
  const softRules = rules.filter(r => r.action === 'soft_flag');

  return (
    <div className='space-y-4' data-testid='admin-moderation-tab'>
      <Card className='p-4'>
        <div className='flex items-start gap-3'>
          <ShieldAlert className='h-5 w-5 text-primary mt-0.5 shrink-0' />
          <div>
            <div className='text-lg font-semibold'>Content moderation rules</div>
            <p className='text-sm text-muted-foreground mt-0.5'>
              Configure which content patterns are blocked outright (<strong>hard reject</strong>)
              or routed to the moderator queue (<strong>soft flag</strong>). Applied to listing
              titles &amp; descriptions, reviews, inquiries, and profile fields.
              Staff (moderator / admin) always bypass these rules.
            </p>
          </div>
        </div>
      </Card>

      <Card className='p-5 space-y-3'>
        <div className='flex items-center gap-2'>
          <XCircle className='h-4 w-4 text-destructive' />
          <div className='font-semibold'>Hard reject rules</div>
          <Badge variant='outline' className='text-[10px]'>400 error</Badge>
        </div>
        {loading ? (
          <div className='flex items-center gap-2 text-sm text-muted-foreground py-3'><Loader2 className='h-4 w-4 animate-spin' /> Loading…</div>
        ) : (
          <div className='divide-y'>
            {hardRules.map(r => (
              <div key={r.key} className='py-3 flex items-start gap-4' data-testid={`mod-rule-${r.key}`}>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='font-medium text-sm'>{r.label}</span>
                    <code className='text-[10px] bg-muted px-1 py-0.5 rounded font-mono'>{r.key}</code>
                  </div>
                  <p className='text-xs text-muted-foreground mt-0.5'>{r.description}</p>
                </div>
                <Switch
                  checked={r.enabled}
                  disabled={busyKey === r.key}
                  onCheckedChange={(v) => toggle(r.key, v)}
                  data-testid={`mod-rule-switch-${r.key}`}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className='p-5 space-y-3'>
        <div className='flex items-center gap-2'>
          <Flag className='h-4 w-4 text-amber-600' />
          <div className='font-semibold'>Soft flag rules</div>
          <Badge variant='outline' className='text-[10px]'>routed to mod queue</Badge>
        </div>
        {!loading && (
          <div className='divide-y'>
            {softRules.map(r => (
              <div key={r.key} className='py-3 flex items-start gap-4' data-testid={`mod-rule-${r.key}`}>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='font-medium text-sm'>{r.label}</span>
                    <code className='text-[10px] bg-muted px-1 py-0.5 rounded font-mono'>{r.key}</code>
                  </div>
                  <p className='text-xs text-muted-foreground mt-0.5'>{r.description}</p>
                </div>
                <Switch
                  checked={r.enabled}
                  disabled={busyKey === r.key}
                  onCheckedChange={(v) => toggle(r.key, v)}
                  data-testid={`mod-rule-switch-${r.key}`}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className='p-5 space-y-3' data-testid='mod-scan-tester'>
        <div className='flex items-center gap-2'>
          <Play className='h-4 w-4 text-primary' />
          <div className='font-semibold'>Test my copy</div>
        </div>
        <p className='text-xs text-muted-foreground'>
          Paste sample listing / review text below to see what the current rules would flag.
          Nothing is saved.
        </p>
        <div className='grid gap-3'>
          <div>
            <Label htmlFor='scan-title' className='text-xs'>Title</Label>
            <Input id='scan-title' value={scanForm.title} onChange={e => setScanForm(f => ({ ...f, title: e.target.value }))} placeholder='Enter listing title…' data-testid='mod-scan-title' />
          </div>
          <div>
            <Label htmlFor='scan-desc' className='text-xs'>Description</Label>
            <Textarea id='scan-desc' rows={3} value={scanForm.description} onChange={e => setScanForm(f => ({ ...f, description: e.target.value }))} placeholder='Enter description text…' data-testid='mod-scan-desc' />
          </div>
          <div>
            <Label htmlFor='scan-body' className='text-xs'>Body (reviews / inquiries)</Label>
            <Textarea id='scan-body' rows={2} value={scanForm.body} onChange={e => setScanForm(f => ({ ...f, body: e.target.value }))} placeholder='Enter review body or inquiry text…' data-testid='mod-scan-body' />
          </div>
          <div>
            <Button onClick={runScan} disabled={scanning} data-testid='mod-scan-run'>
              {scanning ? 'Scanning…' : 'Run scan'}
            </Button>
          </div>
        </div>
        {scanResult && (
          <div className='mt-3 rounded-md border p-3 bg-secondary/40' data-testid='mod-scan-result'>
            {scanResult.clean ? (
              <div className='flex items-center gap-2 text-sm text-emerald-700'>
                <CheckCircle2 className='h-4 w-4' /> Content is clean — no hard-reject or soft-flag matches.
              </div>
            ) : (
              <div className='space-y-2 text-sm'>
                {scanResult.hard_matches.length > 0 && (
                  <div>
                    <div className='font-medium text-destructive flex items-center gap-1'><XCircle className='h-4 w-4' /> Hard rejects</div>
                    <ul className='mt-1 space-y-1 text-xs'>
                      {scanResult.hard_matches.map((m, i) => (
                        <li key={i}>
                          <Badge variant='destructive' className='mr-1 text-[10px]'>{m.rule_key}</Badge>
                          <span className='text-muted-foreground'>{m.field}:</span> <code className='bg-background px-1 py-0.5 rounded text-[11px]'>{m.sample}</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {scanResult.soft_flags.length > 0 && (
                  <div>
                    <div className='font-medium text-amber-700 flex items-center gap-1'><Flag className='h-4 w-4' /> Soft flags</div>
                    <div className='mt-1 flex flex-wrap gap-1'>
                      {scanResult.soft_flags.map(k => <Badge key={k} variant='outline' className='text-[10px]'>{k}</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminModerationTab;
